import {
    BadgePercent,
    Check,
    Clock,
    CreditCard,
    Droplets,
    MessageCircle,
    ShieldCheck,
    Star,
    Wallet,
} from "lucide-react";
import { Eyebrow, Reveal } from "./motion";

const WA_JOIN =
    "https://wa.me/6285715550050?text=" +
    encodeURIComponent("Halo DigiKlin! Saya mau daftar member DigiKlin.");

const CARD_STYLES = {
    silver: "linear-gradient(135deg, #e8eaed 0%, #a8afb8 55%, #c6cbd2 100%)",
    gold: "linear-gradient(135deg, #f3d572 0%, #c9962b 55%, #eac24f 100%)",
    platinum: "linear-gradient(135deg, #5c6675 0%, #232b38 55%, #6e7889 100%)",
};

const TIER_SHADOW = {
    silver: "shadow-tier-silver",
    gold: "shadow-tier-gold",
    platinum: "shadow-tier-platinum",
};

function MemberCardBadge({ tier }) {
    return (
        <span
            data-testid={`member-card-badge-${tier}`}
            className="relative inline-flex h-11 w-[68px] shrink-0 items-center justify-center overflow-hidden rounded-xl shadow-md ring-1 ring-black/10"
            style={{ background: CARD_STYLES[tier] }}
            aria-label={`Kartu Member ${tier}`}
        >
            <span className="absolute left-2 top-2 h-2.5 w-3.5 rounded-[3px] bg-white/75" />
            <span className="absolute bottom-1.5 left-2 flex gap-1">
                <span className="h-1 w-4 rounded-full bg-white/60" />
                <span className="h-1 w-2.5 rounded-full bg-white/45" />
            </span>
            <Droplets
                size={13}
                className="absolute bottom-1 right-1.5 text-white/85"
                strokeWidth={2.6}
            />
        </span>
    );
}

const JOIN_ITEMS = [
    "Saldo E-Money Rp 100.000",
    "Tas Laundry",
    "Membership Card",
    "Voucher diskon kerjasama merchant (jika ada)",
];

const TIERS = [
    {
        id: "silver",
        name: "Member Silver",
        discount: "5%",
        topup: "Top-up saldo kelipatan Rp 50.000",
    },
    {
        id: "gold",
        name: "Member Gold",
        discount: "10%",
        topup: "Top-up saldo minimal Rp 500.000",
    },
    {
        id: "platinum",
        name: "Member Platinum",
        discount: "20%",
        topup: "Top-up saldo minimal Rp 1.000.000",
    },
];

const BENEFITS = [
    {
        icon: BadgePercent,
        title: "Kiloan lebih murah",
        desc: "Harga laundry kiloan lebih murah sesuai level member",
    },
    {
        icon: CreditCard,
        title: "Kartu Member",
        desc: "Diskon di merchant-merchant yang bekerjasama",
    },
    {
        icon: Clock,
        title: "Saldo aktif 180 hari",
        desc: "Masa aktif saldo E-Money 180 hari dari transaksi terakhir",
    },
    {
        icon: Star,
        title: "Point Reward",
        desc: "Segera hadir untuk member DigiKlin",
        soon: true,
    },
];

const SK = [
    "Saldo E-Money hanya bisa dipakai di DigiKlin.",
    "Sisa saldo TIDAK DAPAT diuangkan kembali.",
    "Pembayaran service harus menggunakan E-Money; jika tidak, dikenakan tarif Non-Member. Wajib pelunasan di awal.",
    "Wajib mengisi Formulir Member secara lengkap.",
    "Dengan menjadi Member DigiKlin, customer memahami dan menyetujui Syarat & Ketentuan yang berlaku di DigiKlin.",
];

export default function MemberSection() {
    return (
        <section id="member" className="relative py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-6">
                <Reveal>
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <Eyebrow>Member DigiKlin</Eyebrow>
                            <h2 className="mt-5 max-w-xl font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
                                Jadi member, makin hemat.
                            </h2>
                        </div>
                        <a
                            data-testid="member-cta"
                            href={WA_JOIN}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-fit items-center gap-2 rounded-full bg-[#22c55e] px-7 py-3.5 text-sm font-bold text-white shadow-brand transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#16a34a]"
                        >
                            <MessageCircle size={17} />
                            Daftar Member via WhatsApp
                        </a>
                    </div>
                </Reveal>

                {/* Joining fee banner */}
                <Reveal delay={0.1}>
                    <div
                        data-testid="member-join-banner"
                        className="relative mt-12 overflow-hidden rounded-3xl bg-ink p-8 sm:p-10"
                    >
                        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand/20 blur-3xl" />
                        <div className="relative grid items-center gap-8 lg:grid-cols-2">
                            <div>
                                <span className="inline-flex rounded-full bg-cherry px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white">
                                    Joining Fee Rp 100.000
                                </span>
                                <h3 className="mt-4 font-display text-2xl font-extrabold text-white sm:text-3xl">
                                    Sekali daftar, langsung untung.
                                </h3>
                                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60">
                                    Dengan membayar Joining Fee, Anda langsung
                                    mendapatkan paket berikut:
                                </p>
                            </div>
                            <ul className="grid gap-3 sm:grid-cols-2">
                                {JOIN_ITEMS.map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-2.5 rounded-2xl bg-white/5 px-4 py-3.5 text-sm font-semibold text-white/85 ring-1 ring-white/10"
                                    >
                                        <Check
                                            size={15}
                                            strokeWidth={3}
                                            className="mt-0.5 shrink-0 text-brand-bright"
                                        />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>

                {/* Tiers */}
                <div className="mt-8 grid gap-6 md:grid-cols-3">
                    {TIERS.map((tier, i) => (
                        <Reveal key={tier.id} delay={i * 0.08} className="h-full">
                            <div
                                data-testid={`member-tier-${tier.id}`}
                                className={`flex h-full flex-col rounded-3xl border border-border bg-white p-8 transition-all duration-300 hover:-translate-y-1 sm:p-10 ${TIER_SHADOW[tier.id]}`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <h3 className="font-display text-xl font-bold text-ink">
                                        {tier.name}
                                    </h3>
                                    <MemberCardBadge tier={tier.id} />
                                </div>
                                <p className="mt-6 flex items-baseline gap-2">
                                    <span className="font-display text-5xl font-extrabold tracking-tight text-brand">
                                        −{tier.discount}
                                    </span>
                                    <span className="text-sm font-bold text-ink/50">
                                        semua layanan kiloan
                                    </span>
                                </p>
                                <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-ink/60">
                                    <Wallet size={15} className="text-brand" />
                                    {tier.topup}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                {/* Benefits */}
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {BENEFITS.map((b, i) => (
                        <Reveal key={b.title} delay={i * 0.06} className="h-full">
                            <div
                                data-testid={`member-benefit-${i}`}
                                className="h-full rounded-3xl border border-border bg-white p-7 shadow-brand transition-all duration-300 hover:-translate-y-1 hover:shadow-brand-lg"
                            >
                                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-mist text-brand">
                                    <b.icon size={20} />
                                </span>
                                <h4 className="mt-4 flex items-center gap-2 font-display text-base font-bold text-ink">
                                    {b.title}
                                    {b.soon && (
                                        <span className="rounded-full bg-cherry/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cherry">
                                            Segera
                                        </span>
                                    )}
                                </h4>
                                <p className="mt-2 text-sm leading-relaxed text-ink/55">
                                    {b.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                {/* Terms */}
                <Reveal delay={0.1}>
                    <div
                        data-testid="member-terms"
                        className="mt-8 rounded-3xl border border-border bg-[#F9FAFB] p-8 sm:p-10"
                    >
                        <h4 className="flex items-center gap-2 font-display text-base font-bold text-ink">
                            <ShieldCheck size={18} className="text-brand" />
                            Syarat & Ketentuan Member
                        </h4>
                        <ul className="mt-5 space-y-2.5">
                            {SK.map((s) => (
                                <li
                                    key={s}
                                    className="flex items-start gap-2.5 text-sm leading-relaxed text-ink/60"
                                >
                                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand/50" />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
